import { Decimal as BaseDecimal } from "decimal.js-light";
import {
  type AmountInput,
  type BillingCurrency,
  type CurrencyExchangeRateInput,
  TransactionType,
} from "elephant-web/gql";
import { InvoiceValidationError } from "./invoice-source";

const Decimal = BaseDecimal.clone({ precision: 64 });

export type CurrencyExchangeRate = CurrencyExchangeRateInput;

export type CurrencyExchangeSide = AmountInput;

export type InvoiceCurrencyExchange = {
  from: CurrencyExchangeSide;
  to: CurrencyExchangeSide;
  exchangeRate: CurrencyExchangeRate;
};

export const otherCurrencyOfPair = (
  rate: Pick<CurrencyExchangeRate, "baseCurrency" | "quoteCurrency">,
  currency: BillingCurrency,
): BillingCurrency | null => {
  if (rate.baseCurrency === rate.quoteCurrency) return null;
  if (currency === rate.baseCurrency) return rate.quoteCurrency;
  if (currency === rate.quoteCurrency) return rate.baseCurrency;
  return null;
};

const parseRate = (rate: CurrencyExchangeRate): BaseDecimal => {
  let parsed: BaseDecimal;
  try {
    parsed = new Decimal(rate.rate);
  } catch {
    throw new InvoiceValidationError("Exchange rate must be a decimal");
  }
  if (!parsed.gt(0)) {
    throw new InvoiceValidationError("Exchange rate must be positive");
  }
  return parsed;
};

const convert = (
  value: number,
  fromCurrency: BillingCurrency,
  rate: CurrencyExchangeRate,
): BaseDecimal => {
  const parsed = parseRate(rate);
  if (fromCurrency === rate.quoteCurrency) {
    return new Decimal(value).div(parsed);
  }
  if (fromCurrency === rate.baseCurrency) {
    return new Decimal(value).mul(parsed);
  }
  throw new InvoiceValidationError(
    "Currency must be one of the exchange rate's currencies",
  );
};

export const exchangedToInvoiceValue = (
  exchangedValue: number,
  exchangedCurrency: BillingCurrency,
  rate: CurrencyExchangeRate,
): number =>
  convert(exchangedValue, exchangedCurrency, rate)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();

export const invoiceToExchangedValue = (
  invoiceValue: number,
  exchangedCurrency: BillingCurrency,
  rate: CurrencyExchangeRate,
): number => {
  const invoiceCurrency = otherCurrencyOfPair(rate, exchangedCurrency);
  if (!invoiceCurrency) {
    throw new InvoiceValidationError(
      "Currency must be one of the exchange rate's currencies",
    );
  }
  return convert(invoiceValue, invoiceCurrency, rate)
    .toDecimalPlaces(0, Decimal.ROUND_CEIL)
    .toNumber();
};

const isSmallestCoveringExchange = (
  invoiceValue: number,
  exchangedValue: number,
  exchangedCurrency: BillingCurrency,
  rate: CurrencyExchangeRate,
): boolean =>
  convert(exchangedValue - 1, exchangedCurrency, rate).lt(invoiceValue) &&
  convert(exchangedValue, exchangedCurrency, rate).gte(invoiceValue);

export const applyExchangedToDue = ({
  exchangedValue,
  exchangedCurrency,
  rate,
  due,
}: {
  exchangedValue: number;
  exchangedCurrency: BillingCurrency;
  rate: CurrencyExchangeRate;
  due: number;
}): number | null => {
  const converted = exchangedToInvoiceValue(
    exchangedValue,
    exchangedCurrency,
    rate,
  );
  if (converted <= due) return converted;
  return isSmallestCoveringExchange(
    due,
    exchangedValue,
    exchangedCurrency,
    rate,
  )
    ? due
    : null;
};

export type SelectedCurrencyExchange = {
  currency: BillingCurrency;
  rate: CurrencyExchangeRate;
};

export const invoiceValuesOfRows = ({
  rows,
  due,
}: {
  rows: readonly { value: number; exchange?: SelectedCurrencyExchange }[];
  due: number;
}): number[] => {
  let remaining =
    due -
    rows.reduce((total, row) => (row.exchange ? total : total + row.value), 0);
  return rows.map(({ value, exchange }) => {
    if (!exchange) return value;
    const invoiceValue =
      applyExchangedToDue({
        exchangedValue: value,
        exchangedCurrency: exchange.currency,
        rate: exchange.rate,
        due: Math.max(0, remaining),
      }) ?? exchangedToInvoiceValue(value, exchange.currency, exchange.rate);
    remaining -= invoiceValue;
    return invoiceValue;
  });
};

const sidesByDirection = (
  { from, to }: Pick<InvoiceCurrencyExchange, "from" | "to">,
  type: TransactionType,
): { invoiceSide: CurrencyExchangeSide; exchanged: CurrencyExchangeSide } =>
  type === TransactionType.Refund
    ? { invoiceSide: from, exchanged: to }
    : { invoiceSide: to, exchanged: from };

export const transactionTypeOfExchange = (
  { from }: Pick<InvoiceCurrencyExchange, "from">,
  invoiceCurrency: BillingCurrency,
): typeof TransactionType.Payment | typeof TransactionType.Refund =>
  from.currency === invoiceCurrency
    ? TransactionType.Refund
    : TransactionType.Payment;

export const exchangedSideOf = (
  currencyExchange: Pick<InvoiceCurrencyExchange, "from" | "to">,
  type: TransactionType,
): CurrencyExchangeSide => sidesByDirection(currencyExchange, type).exchanged;

export const buildCurrencyExchange = ({
  type,
  invoiceSide,
  exchanged,
  exchangeRate,
}: {
  type: typeof TransactionType.Payment | typeof TransactionType.Refund;
  invoiceSide: CurrencyExchangeSide;
  exchanged: CurrencyExchangeSide;
  exchangeRate: CurrencyExchangeRate;
}): InvoiceCurrencyExchange =>
  type === TransactionType.Refund
    ? { from: invoiceSide, to: exchanged, exchangeRate }
    : { from: exchanged, to: invoiceSide, exchangeRate };
